/**
 * The AI chat panel — transcript + composer + confirmation flow, hosted by the
 * right-side drawer. Everything here is driven by AiAssistantProvider context;
 * the drawer owns only positioning, header and close mechanics. The bubbles,
 * tool rows, composer and empty state are the shared `chat/*` components (also
 * used by the in-builder AiBuilderPanel).
 *
 * Mount-keyed effects: the panel is only MOUNTED while visible, so the mount
 * effect (pin + scroll-to-latest) is the right hook; the Composer auto-focuses.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { IconArrowDown, IconSparkles } from "@tabler/icons-react";
import { useAiAssistant } from "@/providers/AiAssistantProvider";
import { clearVeloHandoff, readVeloHandoff, type VeloHandoff } from "@/lib/ai/velo-handoff";
import { isNearBottom } from "./drawerUtils";
import { MessageBubble } from "./chat/MessageBubble";
import { ToolStepRow } from "./chat/ToolStepRow";
import { ToolStepGroup } from "./chat/ToolStepGroup";
import { groupTranscript } from "./chat/groupTranscript";
import { ConfirmationCard } from "./chat/ConfirmationCard";
import { SuggestionChips } from "./chat/SuggestionChips";
import { ChatJobProgress } from "./chat/ChatJobProgress";
import { AiDisclaimer } from "./chat/AiDisclaimer";
import { Composer } from "./chat/Composer";
import { EmptyState } from "./chat/EmptyState";
import { useCompanyRoleString } from "@/hooks/useCompanyRoleString";
import { useEventCompanyRole } from "@/lib/query/hooks/useEventEntitlements";
import { hasQuotaRejection, useAiCreditsStatus } from "@/hooks/useAiCreditsStatus";
import { AiCreditsNotice } from "./AiCreditsNotice";
import { useNavigate } from "react-router-dom";
import { isRetryableAiError } from "@/lib/ai/messageLimit";

export function AiChatPanel() {
  const t = useTranslations("aiAssistant");
  const navigate = useNavigate();
  // Fails closed to VIEWER while loading — the buy chip stays hidden until
  // the real role is known; the backend purchase endpoint is the true gate.
  const companyRole = useCompanyRoleString();
  const {
    messages,
    streaming,
    scopeKind,
    scopeEventId,
    stopStreaming,
    retryLast,
    sendMessage,
    pendingConfirmation,
    confirmPendingTool,
  } = useAiAssistant();
  // Credits are the EVENT's company's in event scope — only its OWNER can buy
  // more; the login-company role decides in company scope (see AiQuotaActions).
  const eventCompany = useEventCompanyRole(scopeEventId ?? null);
  const canBuyCredits = scopeEventId ? eventCompany.isCompanyOwner : companyRole === "OWNER";

  // Forewarning strip above the composer. The drawer header owns the ring;
  // this is the part that speaks up on its own before the wall.
  const credits = useAiCreditsStatus({ rejected: hasQuotaRejection(messages) });

  // Wizard→event handoff notice: shown on the empty transcript of a freshly
  // wizard-created event, so the user knows Velo is already primed with the
  // interview (the FIRST send carries the brief — see AiAssistantProvider).
  // Dismiss = true opt-out: the one-shot payload is dropped entirely and the
  // event chat starts cold, never re-forced.
  const [handoff, setHandoff] = useState<VeloHandoff | null>(null);
  useEffect(() => {
    setHandoff(scopeEventId ? readVeloHandoff(scopeEventId) : null);
  }, [scopeEventId]);
  const dismissHandoff = () => {
    if (scopeEventId) clearVeloHandoff(scopeEventId);
    setHandoff(null);
  };
  const panelReduceMotion = useReducedMotion();
  // "Pinned" = the user is (near) the bottom, so streaming may auto-scroll.
  // Scrolling up unpins; the jump-to-latest pill re-pins.
  const [pinned, setPinned] = useState(true);
  const listRef = useRef<HTMLDivElement | null>(null);

  // Keep the newest message in view while tokens stream in — but only while the
  // user hasn't scrolled up to read something earlier. rAF-coalesced: the
  // scrollHeight read forces layout, so do it once per frame, not per delta.
  useEffect(() => {
    if (!pinned) return;
    const raf = requestAnimationFrame(() => {
      const list = listRef.current;
      if (list) list.scrollTop = list.scrollHeight;
    });
    return () => cancelAnimationFrame(raf);
  }, [messages, pinned]);

  // On MOUNT (the panel is only mounted while its shell is visible): pin and
  // scroll to the latest. (The Composer auto-focuses the input.)
  useEffect(() => {
    setPinned(true);
    const list = listRef.current;
    if (list) list.scrollTop = list.scrollHeight;
  }, []);

  const handleScroll = () => {
    const list = listRef.current;
    if (list) setPinned(isNearBottom(list));
  };

  const jumpToLatest = () => {
    const list = listRef.current;
    // Instant jump on purpose: a smooth scroll fires intermediate scroll events
    // that read as "not at bottom" and flicker the pill back in.
    if (list) list.scrollTop = list.scrollHeight;
    setPinned(true);
  };

  // Index of the last assistant turn — Regenerate shows only there.
  const lastAssistantIndex = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") return i;
    }
    return -1;
  }, [messages]);
  const lastIndex = messages.length - 1;

  // Collapse consecutive tool rows into one toggle so a long chain of steps
  // ("add 15 tables") doesn't bury the conversation.
  const items = useMemo(() => groupTranscript(messages), [messages]);

  // Screen-reader announcer: streaming token updates would spam a live
  // transcript, so instead publish ONE atomic message per turn — "Thinking…"
  // while generating, then the finished assistant reply (or its error) once.
  const lastMsg = messages[lastIndex];
  const liveAnnounce = streaming
    ? t("thinking")
    : lastMsg?.role === "assistant"
      ? lastMsg.errorCode
        ? t.has(`errors.${lastMsg.errorCode}`)
          ? t(`errors.${lastMsg.errorCode}`)
          : t("errors.aiUnavailable")
        : (lastMsg.content ?? "")
      : "";

  return (
    <>
      {/* SR-only turn announcer (see liveAnnounce). */}
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
        {liveAnnounce}
      </div>
      {/* Messages */}
      <div className="relative min-h-0 flex-1 flex flex-col">
        <div
          ref={listRef}
          data-testid="ai-assistant-messages"
          aria-label={t("transcriptLabel")}
          className="min-h-0 flex-1 overflow-y-auto p-4 flex flex-col gap-3.5"
          onScroll={handleScroll}
        >
          {messages.length === 0 ? (
            <>
              {handoff && (
                <div
                  data-testid="ai-handoff-notice"
                  className="shrink-0 rounded-xl border outline-quaternary surface-secondary p-3 flex items-start gap-2"
                >
                  <IconSparkles size={16} className="font-blue shrink-0 mt-0.5" />
                  <div className="flex flex-col gap-0.5 min-w-0 grow text-left">
                    <span className="text-sm font-medium">{t("handoff.title")}</span>
                    <span className="text-xs font-tertiary">
                      {t("handoff.body", { event: handoff.eventName })}
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={dismissHandoff}
                    className="text-xs font-accent shrink-0 cursor-pointer"
                  >
                    {t("handoff.dismiss")}
                  </button>
                </div>
              )}
              <EmptyState variant="chat" scopeKind={scopeKind} onPick={(text) => sendMessage(text)} />
            </>
          ) : (
            <>
              {items.map((item) =>
                item.kind === "tools" ? (
                  item.tools.length === 1 ? (
                    <ToolStepRow key={item.tools[0].id} message={item.tools[0]} />
                  ) : (
                    <ToolStepGroup key={item.tools[0].id} tools={item.tools} />
                  )
                ) : (
                  <MessageBubble
                    key={item.message.id}
                    message={item.message}
                    isLast={item.index === lastIndex}
                    // Price badge: the last ASSISTANT bubble, which is NOT the last
                    // message when a turn ends on a tool row.
                    isLastAssistant={item.index === lastAssistantIndex}
                    canRetry={
                  !streaming && item.index === lastIndex && isRetryableAiError(item.message.errorCode)
                }
                    canRegenerate={
                      !streaming &&
                      item.index === lastAssistantIndex &&
                      item.index === lastIndex &&
                      !item.message.errorCode &&
                      !!item.message.content
                    }
                    onRetry={retryLast}
                  />
                ),
              )}
              {/* Follow-up / recovery chips under the last assistant turn:
                  recovery actions on a failed turn, else the model's proposed
                  next steps. Hidden while streaming or while a confirmation is
                  pending (the card is the action then). */}
              {!streaming && !pendingConfirmation && lastMsg?.role === "assistant" && (
                lastMsg.errorCode === "aiQuotaExceeded" ? (
                  // Out of AI credits: retry/reload can't help. Company OWNERs
                  // (the only role allowed on POST /billing/purchase) get a
                  // buy-credits path; everyone else keeps the bubble's
                  // "continue manually" message with no dead-end chip.
                  canBuyCredits ? (
                    <SuggestionChips
                      ariaLabel={t("suggestionsLabel")}
                      items={[
                        {
                          label: t("buyAiCredits"),
                          onClick: () => navigate("/user/subscription/ai-credits"),
                        },
                      ]}
                    />
                  ) : null
                ) : isRetryableAiError(lastMsg.errorCode) ? (
                  // The bubble already renders an inline "Retry"; add only the
                  // page-reload recovery here (covers a wedged session). Not
                  // for a too-long message: a reload cannot shorten it.
                  <SuggestionChips
                    ariaLabel={t("suggestionsLabel")}
                    items={[
                      { label: t("reloadPage"), onClick: () => window.location.reload() },
                    ]}
                  />
                ) : lastMsg.suggestions && lastMsg.suggestions.length ? (
                  <SuggestionChips
                    ariaLabel={t("suggestionsLabel")}
                    items={lastMsg.suggestions.map((s) => ({
                      label: s,
                      onClick: () => sendMessage(s),
                    }))}
                  />
                ) : null
              )}
              {pendingConfirmation && !streaming && (
                <ConfirmationCard
                  // A fresh card per pending call: the W8 legal-basis tick is
                  // the card's own state and must never carry over to the next
                  // import the model asks for.
                  key={pendingConfirmation.toolUseId}
                  action={pendingConfirmation}
                  onResolve={confirmPendingTool}
                />
              )}
            </>
          )}
        </div>
        <AnimatePresence>
          {!pinned && messages.length > 0 && (
            <motion.button
              key="jump-to-latest"
              className="absolute bottom-3 left-1/2 flex items-center gap-1 rounded-full surface-primary border outline-quaternary shadow-md px-3 py-1.5 text-xs font-medium hover:surface-secondary transition-colors cursor-pointer"
              style={{ x: "-50%" }}
              initial={panelReduceMotion ? { opacity: 0 } : { opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={panelReduceMotion ? { opacity: 0 } : { opacity: 0, y: 4 }}
              transition={{ duration: panelReduceMotion ? 0 : 0.12 }}
              onClick={jumpToLatest}
            >
              <IconArrowDown size={14} />
              {t("jumpToLatest")}
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <ChatJobProgress />
      <AiCreditsNotice status={credits} />
      <Composer
        variant="chat"
        streaming={streaming}
        onSend={sendMessage}
        onStop={stopStreaming}
        autoFocus
        placeholder={scopeKind === "company" ? t("inputPlaceholderCompany") : t("inputPlaceholder")}
        ariaLabel={scopeKind === "company" ? t("inputPlaceholderCompany") : t("inputPlaceholder")}
      />
      <AiDisclaimer />
    </>
  );
}
